import React from "react";

/**
 * Real Google reviews, verbatim from clients' Google Business profiles.
 * Used across the site as authentic social proof.
 */
export const GOOGLE_REVIEWS = [
  {
    id: "leanne-druhan",
    body:
      "So happy to have been introduced to Innate Perspectives. Easy to use and a valuable tool to help with understanding self and others. Has made communication and articulation of thought and feelings a breeze. Totally recommend.",
    name: "Leanne Druhan",
    role: "",
  },
  {
    id: "matt-hall",
    body:
      "Brooklyn's KOLBE coaching offered a valuable insight into how we respond to everyday situations under pressure. This knowledge not only guided me on where my efforts would be most impactful, but it also helped me understand what to expect from my colleagues. Gaining clarity on my KOLBE strengths and challenges fostered a more harmonious and calm work environment, where we could all excel in roles that played to our strengths. Brooklyn's honest, engaging coaching style, combined with her deep understanding of people and what motivates them, is truly her superpower.",
    name: "Matt Hall",
    role: "Small Business Owner",
  },
  {
    id: "keane-marsh",
    body:
      "Brooklyn's perspectives was a complete game changer for me. Working with Innate Perspectives I was able to uncover how to use my own values to get more done. I think very differently to most, so being able to explore that, see the power of that, has been amazing. It's helped me also communicate more effectively in the workplace. Very grateful to have crossed paths with Brooklyn. I feel extremely energised and inspired speaking with her. Would highly recommend her to anyone looking for a competitive edge when it comes to connection and communication, in all aspects of life.",
    name: "Keane Marsh",
    role: "Local Guide · 40 reviews",
  },
  {
    id: "tommy-anderson",
    body:
      "Brooklyn helped myself and my Wife by uncovering what lied underneath the surface, this is an experience like no other. I feel truly blessed to come across such a caring person who takes a scientific approach to mental and physical health. This was a big learn, highly recommend.",
    name: "Tommy Anderson",
    role: "5 reviews",
  },
  {
    id: "jessica-cuffe",
    body:
      "I had my first session with Brooklyn today and words couldn't really describe the session. I had zero expectations and was truly amazed by the movement I felt emotionally and mentally. Events from recent past that had been difficult to accept and process on a deeper level no longer held the same grip on my day to day life and self worth. Highly recommend for anyone wanting to tap in and see/feel immediate weight lifted! Thanks Brooklyn.",
    name: "Jessica Cuffe",
    role: "3 reviews",
  },
  {
    id: "lucy-tirrell",
    sourceKind: "google_cbf",
    body:
      "If I could give more than 5 stars, I would. The past few months I've been working with Brooklyn to overcome one of the most draining challenges of my life. My experience has been transformational in ways I couldn't have imagined. I feel as if I've been unchained, fed a wholesome meal and sent free to play in the warm sunshine, completely free to choose my life ahead. The work not only feels safe, achievable and gentle with Brooklyn, but it feels fun! Today, I can confidently say I've got my groove back!",
    name: "Lucy Tirrell",
    role: "CBF Member",
  },
  {
    id: "joanne-williams",
    sourceKind: "google_cbf",
    body:
      "I've been seeing Brooklyn for about a year now. I am such a different person today. I am able to be in situations that I used to be uncomfortable in and handle situations and people without anxiety or negativity. I handle stress and the unknown without any thought. I still don't fully understand HOW it all works I just know it does. Even my friends have commented on how much I have changed and mellowed out.",
    name: "Joanne Williams",
    role: "CBF Member",
  },
  {
    id: "daryl-seager",
    sourceKind: "peer",
    body:
      "Brooklyn is one of the most giving people I have met. She has a genuine curiosity in people and always sees the best in them. She has a way of also seeing to the heart of any issue you are facing and has an encyclopedic knowledge of a host of different modalities. She's one of the people in my life who I can truly be 'me' with, that's something special. Some people have a way of showing up. Brooklyn's one of them. She doesn't try to impress you. She listens. She asks sharp questions. She brings warmth into even the most complex conversations. That kind of presence is rare.",
    name: "Daryl Seager",
    role: "Lead Confidently. Live Joyfully.",
  },
];

/* Inline 5-star row, gold filled stars */
const StarRow = () => (
  <div className="flex items-center gap-1.5 text-gold" aria-label="5 out of 5 stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <svg key={i} viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]" aria-hidden="true">
        <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1z" />
      </svg>
    ))}
  </div>
);

/* Google "G" logo (multicolor) */
const GoogleG = ({ size = 14 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.2 5.2c-.4.4 6.8-5 6.8-14.9 0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);

/* Google Verified badge */
export const GoogleVerifiedBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.14em] uppercase text-ink/55 ${className}`}
    title="Verified Google review"
  >
    <GoogleG size={13} />
    <span>Google Verified Review</span>
  </span>
);

/* Source-specific verification badge. Google, Google + CBF, or Peer Review */
const SourceBadge = ({ kind }) => {
  if (kind === "peer") {
    return (
      <span
        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.14em] uppercase text-ink/55"
        title="Peer review from a fellow practitioner"
      >
        <span className="block w-1.5 h-1.5 rounded-full bg-gold" aria-hidden="true" />
        Peer Review
      </span>
    );
  }
  if (kind === "google_cbf") {
    return (
      <span
        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.14em] uppercase text-ink/55"
        title="Verified Google review from a CBF member"
      >
        <GoogleG size={13} />
        Google · CBF Member
      </span>
    );
  }
  return <GoogleVerifiedBadge />;
};

/**
 * Single review card, light editorial style (cream card, gold stars, italic Cormorant body, em-dash attribution).
 * `review.sourceKind` controls the badge: "google" (default), "google_cbf", or "peer".
 */
export default function GoogleReview({ review, testId = "google-review" }) {
  const kind = review.sourceKind || "google";
  return (
    <article
      data-testid={testId}
      className="bg-[#fefdfb] border border-rule p-8 md:p-10 flex flex-col h-full"
    >
      <div className="flex items-center justify-between mb-6">
        <StarRow />
        <SourceBadge kind={kind} />
      </div>
      <blockquote className="font-editorial italic text-[18px] md:text-[20px] leading-[1.55] text-ink/85 font-light mb-6 flex-1">
        {`\u201C${review.body}\u201D`}
      </blockquote>
      <figcaption className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-5 border-t border-rule">
        <span className="block w-7 h-px bg-ink/30 self-center" aria-hidden="true" />
        <span className="font-medium text-ink text-[15px] whitespace-nowrap">{review.name}</span>
        {review.role && (
          <span className="font-light text-ash text-[13px] basis-full sm:basis-auto sm:before:content-['·'] sm:before:mr-1.5 sm:before:text-ink/30">{review.role}</span>
        )}
      </figcaption>
    </article>
  );
}
